import { useState, useEffect } from "react";
import {
  Button,
  Divider,
  Group,
  Modal,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconFileSpreadsheet, IconPrinter, IconReportAnalytics } from "@tabler/icons-react";
import { BlobSaveToFile } from "@/Helpers/SaveToFile";
import {
  generateEvaluationCsv,
  MetricAggregationType,
  RawEvaluationTestResult,
} from "./evaluationReportUtils";

export interface ChatEvaluationExportModalProps {
  opened: boolean;
  onClose: () => void;
  workspaceId: string;
  chatEvaluationTestSetId: string;
  chatEvaluationTestId: string;
  defaultTitle?: string;
  defaultCustomerName?: string;
  rawResults: RawEvaluationTestResult[];
  currentMode: MetricAggregationType;
}

export function ChatEvaluationExportModal({
  opened,
  onClose,
  workspaceId,
  chatEvaluationTestSetId,
  chatEvaluationTestId,
  defaultTitle = "",
  defaultCustomerName = "",
  rawResults,
  currentMode,
}: ChatEvaluationExportModalProps) {
  const [customerName, setCustomerName] = useState(defaultCustomerName);
  const [reportTitle, setReportTitle] = useState(defaultTitle);
  const [renderMarkdown, setRenderMarkdown] = useState(true);

  useEffect(() => {
    if (opened) {
      setCustomerName(defaultCustomerName);
      setReportTitle(defaultTitle);
      setRenderMarkdown(true);
    }
  }, [opened, defaultCustomerName, defaultTitle]);

  const handleOpenPdfReport = () => {
    const params = new URLSearchParams({
      title: reportTitle.trim(),
      customerName: customerName.trim(),
      mode: currentMode,
      markdown: renderMarkdown ? "true" : "false",
    });
    const url = `/workspace/${workspaceId}/chatEvaluationTestSet/${chatEvaluationTestSetId}/test/${chatEvaluationTestId}/report?${params.toString()}`;
    window.open(url, "_blank");
    onClose();
  };

  const handleDownloadCsv = () => {
    try {
      const csvContent = generateEvaluationCsv(rawResults, currentMode);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const safeTitle = (reportTitle.trim() || "評估結果").replace(/[\\/:*?"<>|]/g, "_");
      const fileName = `${safeTitle}_原始數據.csv`;
      BlobSaveToFile(blob, fileName);

      notifications.show({
        title: "匯出成功",
        message: `已成功下載 ${fileName}`,
        color: "teal",
      });
      onClose();
    } catch (e) {
      notifications.show({
        title: "匯出失敗",
        message: "產生 CSV 原始資料時發生錯誤",
        color: "red",
      });
    }
  };

  const modeLabel =
    currentMode === "avg" ? "平均值" : currentMode === "max" ? "最高分" : "最低分";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <ThemeIcon color="blue" variant="light" size="md">
            <IconReportAnalytics size={18} />
          </ThemeIcon>
          <Text fw={600} size="md">
            產出專業評估報告與原始資料
          </Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        <Text size="xs" c="dimmed">
          為客戶量身打造專業的成效評估報告，可直接列印存為 A4 PDF，或下載 Excel
          相容之 CSV 完整評估記錄。
        </Text>

        <TextInput
          label="受測客戶 / 專案名稱"
          description="將顯示於報告封面與標頭"
          placeholder="例如：永慶房屋 / 智慧房產線上諮詢專案"
          value={customerName}
          onChange={(e) => setCustomerName(e.currentTarget.value)}
        />

        <TextInput
          label="報告標題"
          description="報告主要文件名稱"
          placeholder="例如：AI 智慧問答成效評估報告"
          value={reportTitle}
          onChange={(e) => setReportTitle(e.currentTarget.value)}
        />

        <Switch
          size="sm"
          color="blue"
          label="AI 回覆啟用 Markdown 格式渲染"
          description="若 AI 回覆包含條列清單、粗體、程式碼或表格，將以排版美化呈現"
          checked={renderMarkdown}
          onChange={(e) => setRenderMarkdown(e.currentTarget.checked)}
        />

        <Paper p="xs" withBorder bg="gray.0" radius="sm">
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              評估樣本筆數：
            </Text>
            <Text size="xs" fw={600}>
              {rawResults.length} 筆
            </Text>
          </Group>
          <Group justify="space-between" mt={4}>
            <Text size="xs" c="dimmed">
              指標計算模式：
            </Text>
            <Text size="xs" fw={600}>
              {modeLabel}
            </Text>
          </Group>
        </Paper>

        <Divider />

        <Group justify="flex-end" gap="xs">
          <Button
            variant="light"
            color="teal"
            leftSection={<IconFileSpreadsheet size={16} />}
            onClick={handleDownloadCsv}
          >
            下載 Excel (CSV)
          </Button>

          <Button
            variant="filled"
            color="blue"
            leftSection={<IconPrinter size={16} />}
            onClick={handleOpenPdfReport}
          >
            檢視與列印 PDF 報告
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
